import { env } from "@/env";
import { Agent, AgentPaymentConfig } from "@/types/agent";
import { Institution } from "@/types/institutions";
import { Merchant } from "@/types/merchant";
import { PayinAccountsWithAggregatedBalancesResponse } from "@/types/payin-accounts-with-aggregated-balances";
import { Payment } from "@/types/payment";
import {
  GetPaymentIntentResponse,
  GetPaymentIntentsResponse,
} from "@/types/paymentIntent";
import { Payments, Payouts } from "@/types/payments";
import { PaymentsSummary } from "@/types/paymentsSummary";
import { Revenue } from "@/types/revenue";
import { SecretsSummary } from "@/types/appSecretsSummary";
import { GetPaymentDetails } from "@/types/getPaymentDetails";
import dayjs from "dayjs";
import { COMMON_FETCH_HEADERS } from "./utils";
import {
  MerchantRole,
  MerchantRoleId,
  PermissionItem,
} from "@/types/user-management";
import { redirectToSignIn } from "./auth";

type VolumeApiEndpoint =
  | "payments"
  | "payments/summary"
  | `payments/${string}`
  | "revenue"
  | "merchants"
  | `merchants/${string}`
  | `paymentintents`
  | `paymentintents/${string}`
  | "institutions"
  | "accounts"
  | `accounts/${string}`
  | "roles"
  | "agents"
  | `agents/${string}`
  | `agents/${string}/payment-config`
  | `agents/${string}/payment-config/history`
  | `/merchants/${string}/applications/${string}/payin-accounts-with-aggregated-balances`
  | `/merchants/${string}/applications/${string}/secret`
  | `/merchants/${string}/applications/${string}/outbound-payments`
  | `/merchants/${string}/applications/${string}/external-payment/send-email`
  | `/merchants/${string}/applications/${string}/external-payment/send-webhook`
  | `/merchants/${string}/applications/${string}/payouts`
  | `/merchants/${string}/applications/${string}/payouts/schedule`;

type VolumeApiMethod = "GET" | "POST" | "PUT";
type VolumeAcceptHeader =
  | "application/vnd.volume.v0.7+json"
  | "application/vnd.volume.v0.8+json"
  | "application/vnd.volume.v1.0+json"
  | "application/json";

type ParamsWithApplicationId<T> = T & { applicationId: string };

interface GetPaymentsParams {
  paymentStatuses: string;
  startDateTimeUtc: string;
  endDateTimeUtc: string;
  currencies: string;
  page: string;
  size: string;
  sort: string;
  paymentTypes: string;
  refundedFilter: string;
  merchantId?: string;
  reference?: string;
  globalSearch?: string;
  amountFrom?: number;
  amountTo?: number;
  includeNewPayments?: boolean;
}
interface GetPayoutsParams {
  size: string;
  page: string;
  currency?: string;
  startDateTimeUtc?: string;
  endDateTimeUtc?: string;
  merchantId: string;
  sort: string;
  globalSearch?: string;
  minAmount?: number;
  maxAmount?: number;
  statuses?: string;
}
interface GetPaymentsSummaryParams {
  sort: string;
  startDateTimeUtc: string;
  endDateTimeUtc?: string;
  paymentStatuses: string;
  merchantId?: string;
}
interface GetRevenueParams {
  sort: string;
  startDateTimeUtc: string;
  endDateTimeUtc?: string;
  periodType: string;
  paymentStatuses: string;
  merchantId?: string;
}
type GetMerchantParams = object;
interface GetRecentPaymentIntentsParams {
  startDateTimeUtc: string;
  showOnlyLinks: string;
  showNotStarted: string;
  size: number;
  page: number;
}
type GetPaymentIntentParams = object;
type GetInstitutionsParams = object;
type GetPaymentParams = object;
type GetPayinAccountsWithAggregatedBalancesParams = object;
type GetApplicationSecretSummaryParams = object;
export type GetMerchantAccountsResponse = {
  userId: string;
  email: string;
  active: boolean;
  name: string;
  createdDate: string;
  permissions: MerchantRoleId[];
  phoneNumber: string | null;
}[];

export const VOLUME_API_VERSION_HEADERS = {
  v07: "application/vnd.volume.v0.7+json",
  v08: "application/vnd.volume.v0.8+json",
  v09: "application/vnd.volume.v0.9+json",
  v1: "application/vnd.volume.v1.0+json",
} as const;

/**
 * Helper methods to interact with the Volume API
 */
export class VolumeApi {
  private accessToken: string;
  private applicationId: string;
  constructor(accessToken: string, applicationId: string) {
    this.accessToken = accessToken;
    this.applicationId = applicationId;
  }

  private async fetch<
    TQueryParameters extends object,
    TApiResponse extends object,
  >({
    endpoint,
    method,
    queryParameters,
    acceptHeader,
    contentTypeHeader,
    xApplicationIdHeader,
    body,
  }: {
    endpoint: VolumeApiEndpoint;
    method: VolumeApiMethod;

    /**
     * Object containing query parameters to be appended to the request URL
     *
     * @example
     * ```js
     * { currency: 'EUR' }
     * ```
     *
     * Would result in a request Params of `/?currency=EUR`
     */
    queryParameters: TQueryParameters;
    acceptHeader?: VolumeAcceptHeader;
    xApplicationIdHeader?: string;
    contentTypeHeader?: string;
    body?: object;
  }) {
    const queryParametersString = Object.entries(queryParameters)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    let init: RequestInit = {
      method,
      headers: {
        ...(this.accessToken !== "" && {
          Authorization: `Bearer ${this.accessToken}`,
        }),
        ...(acceptHeader && { Accept: acceptHeader }),
        ...(contentTypeHeader && { "Content-Type": contentTypeHeader }),
        ...(xApplicationIdHeader && {
          "x-application-id": xApplicationIdHeader,
        }),
        ...COMMON_FETCH_HEADERS,
      },
    };
    if (body) {
      init = {
        body: JSON.stringify(body),
        ...init,
      };
    }

    const response = await fetch(
      `${env.VITE_VOLUME_URL}/api/${endpoint}?${queryParametersString}`,
      init,
    );
    if (response.status === 401) {
      // const nav = Route.useNavigate();
      // nav({ to: "/signin" });
      throw redirectToSignIn(false);
    }
    if (!response.ok) {
      const error = new Error("Failed to fetch data") as Error & {
        status?: number;
      };
      error.status = response.status;
      throw error;
    }
    const json = (await response.json()) as TApiResponse;
    return json;
  }

  /**
   * GET /api/payments
   */
  public getPayments({
    paymentStatuses,
    startDateTimeUtc,
    endDateTimeUtc,
    currencies,
    page,
    size,
    sort,
    merchantId,
    globalSearch,
    amountFrom,
    amountTo,
    paymentTypes,
    refundedFilter,
  }: GetPaymentsParams) {
    return this.fetch<ParamsWithApplicationId<GetPaymentsParams>, Payments>({
      endpoint: "payments",
      method: "GET",
      queryParameters: {
        paymentStatuses,
        startDateTimeUtc: startDateTimeUtc.replace("Z", ""), // TODO: This is inconsistent, cant remove as generate report expects Z
        endDateTimeUtc: endDateTimeUtc.replace("Z", ""),
        currencies,
        page,
        size,
        sort,
        paymentTypes,
        refundedFilter,
        includeNewPayments: true,
        applicationId: this.applicationId,
        ...(globalSearch && { globalSearch }),
        ...(merchantId && { merchantId }),
        ...(amountFrom && { minAmount: amountFrom }),
        ...(amountTo && { maxAmount: amountTo }),
      },
      acceptHeader: VOLUME_API_VERSION_HEADERS.v08,
    });
  }

  /**
   * GET outbound payouts for a given merchant
   */
  public getPayouts({
    size,
    page,
    currency,
    endDateTimeUtc,
    merchantId,
    startDateTimeUtc,
    sort,
    globalSearch,
    minAmount,
    maxAmount,
    statuses,
  }: GetPayoutsParams) {
    return this.fetch<ParamsWithApplicationId<GetPayoutsParams>, Payouts>({
      endpoint: `merchants/${merchantId}/application/${this.applicationId}/outbound-payments`,
      method: "GET",
      queryParameters: {
        applicationId: this.applicationId,
        size,
        page,
        sort,
        merchantId,
        ...(currency && { currency }),
        ...(startDateTimeUtc && {
          startDateTimeUtc: startDateTimeUtc.replace("Z", ""), // TODO: This is inconsistent, cant remove as generate report expects Z
        }),
        ...(endDateTimeUtc && {
          endDateTimeUtc: endDateTimeUtc.replace("Z", ""),
        }),
        ...(globalSearch && { globalSearch }),
        ...(minAmount !== undefined && { minAmount }),
        ...(maxAmount !== undefined && { maxAmount }),
        ...(statuses && { statuses }),
      },
    });
  }

  /**
   * GET /api/payments/summary
   */
  public getPaymentsSummary({
    startDateTimeUtc,
    sort,
    paymentStatuses,
    endDateTimeUtc,
    merchantId,
  }: GetPaymentsSummaryParams) {
    return this.fetch<
      ParamsWithApplicationId<GetPaymentsSummaryParams>,
      PaymentsSummary
    >({
      endpoint: "payments/summary",
      method: "GET",
      queryParameters: {
        applicationId: this.applicationId,
        sort,
        startDateTimeUtc,
        ...(endDateTimeUtc && { endDateTimeUtc }),
        paymentStatuses,
        ...(merchantId && { merchantId }),
      },
      acceptHeader: VOLUME_API_VERSION_HEADERS.v08,
    });
  }

  /**
   * GET /api/revenue
   */
  public getRevenue({
    paymentStatuses,
    periodType,
    sort,
    startDateTimeUtc,
    endDateTimeUtc,
    merchantId,
  }: GetRevenueParams) {
    return this.fetch<ParamsWithApplicationId<GetRevenueParams>, Revenue>({
      endpoint: "revenue",
      method: "GET",
      queryParameters: {
        applicationId: this.applicationId,
        sort,
        startDateTimeUtc,
        endDateTimeUtc,
        periodType,
        paymentStatuses,
        ...(merchantId && { merchantId }),
      },
      acceptHeader: VOLUME_API_VERSION_HEADERS.v08,
    });
  }

  /**
   * GET /api/merchants/{merchantId}
   */
  public getMerchant({ merchantId }: { merchantId: string }) {
    return this.fetch<ParamsWithApplicationId<GetMerchantParams>, Merchant>({
      endpoint: `merchants/${merchantId}`,
      method: "GET",
      queryParameters: {
        applicationId: this.applicationId,
      },
    });
  }

  /**
   * GET /api/paymentintents/{paymentIntentId}
   */
  public getPaymentIntent({ paymentIntentId }: { paymentIntentId: string }) {
    return this.fetch<
      ParamsWithApplicationId<GetPaymentIntentParams>,
      GetPaymentIntentResponse
    >({
      endpoint: `paymentintents/${paymentIntentId}`,
      method: "GET",
      queryParameters: {
        applicationId: this.applicationId,
      },
      xApplicationIdHeader: this.applicationId,
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v07,
    });
  }

  /**
   * GET /api/institutions
   */
  public getInstitutions() {
    return this.fetch<GetInstitutionsParams, Institution[]>({
      endpoint: "institutions",
      method: "GET",
      queryParameters: {},
      acceptHeader: "application/json",
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v07,
      xApplicationIdHeader: this.applicationId,
    });
  }

  /**
   * GET /api/payment
   */
  public getPayment({ paymentId }: { paymentId: string }) {
    return this.fetch<GetPaymentParams, Payment>({
      endpoint: `payments/${paymentId}`,
      method: "GET",
      queryParameters: {},
    });
  }

  public getPaymentDetails({ paymentId }: { paymentId: string }) {
    return this.fetch<object, GetPaymentDetails>({
      endpoint: `payments/${paymentId}/details`,
      method: "GET",
      queryParameters: {},
    });
  }

  /**
   * GET /api/merchants/{merchantId}/applications/{applicationId}/payin-accounts-with-aggregated-balances
   */
  public getPayinAccountsWithAggregateBalances({
    merchantId,
  }: {
    merchantId: string;
  }) {
    return this.fetch<
      GetPayinAccountsWithAggregatedBalancesParams,
      PayinAccountsWithAggregatedBalancesResponse[]
    >({
      endpoint: `merchants/${merchantId}/applications/${this.applicationId}/payin-accounts-with-aggregated-balances`,
      method: "GET",
      queryParameters: {},
    });
  }

  public getApplicationSecretSummary({ merchantId }: { merchantId: string }) {
    return this.fetch<GetApplicationSecretSummaryParams, SecretsSummary>({
      endpoint: `merchants/${merchantId}/applications/${this.applicationId}/secret`,
      method: "GET",
      queryParameters: {},
      acceptHeader: "application/json",
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v07,
    });
  }

  /**
   * GET /api/paymentintents
   */
  public getRecentPaymentIntents({
    applicationId,
    page,
  }: {
    applicationId: string;
    page: number;
  }) {
    const startTime =
      dayjs()
        // .utc()
        .subtract(12, "months")
        .format("YYYY-MM-DDT00:00:00")
        .split("T")[0] + "T00:00:00";

    return this.fetch<
      ParamsWithApplicationId<GetRecentPaymentIntentsParams>,
      GetPaymentIntentsResponse
    >({
      endpoint: `paymentintents`,
      method: "GET",
      queryParameters: {
        applicationId: applicationId,
        startDateTimeUtc: startTime,
        showOnlyLinks: "true",
        showNotStarted: "true",
        size: 20,
        page: page - 1,
      },
      xApplicationIdHeader: this.applicationId,
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v08,
    });
  }

  /**
   * PUT /api/merchants/{merchantId}/applications/{applicationId}/external-payment/send-email
   */
  public shouldSendEmailForExternalPayinPayment({
    merchantId,
    shouldSend,
  }: {
    merchantId: string;
    shouldSend: boolean;
  }) {
    return this.fetch<object, object>({
      endpoint: `merchants/${merchantId}/applications/${this.applicationId}/external-payment/send-email`,
      method: "PUT",
      acceptHeader: VOLUME_API_VERSION_HEADERS.v07,
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v07,
      queryParameters: {},
      body: {
        shouldSend,
      },
    });
  }

  /**
   * PUT /api/merchants/{merchantId}/applications/{applicationId}/send-webhook
   */
  public shouldSendWebhookForExternalPayinPayment({
    merchantId,
    shouldSend,
  }: {
    merchantId: string;
    shouldSend: boolean;
  }) {
    return this.fetch<object, object>({
      endpoint: `merchants/${merchantId}/applications/${this.applicationId}/external-payment/send-webhook`,
      method: "PUT",
      acceptHeader: VOLUME_API_VERSION_HEADERS.v07,
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v07,
      queryParameters: {},
      body: { shouldSend },
    });
  }

  /**
   * GET /api/merchants/{merchantId}/accounts
   * Returns a list of accounts for a given merchant
   * Requires VOLUME_ADMIN or VOLUME_ADMIN_READ permission
   */
  public getMerchantAccounts({ merchantId }: { merchantId: string }) {
    return this.fetch<object, GetMerchantAccountsResponse>({
      endpoint: `merchants/${merchantId}/accounts`,
      method: "GET",
      queryParameters: {},
    });
  }

  /**
   * POST {{api-host}}/api/merchants/{{merchant-id}}/accounts
   */
  public createMerchantAccount({
    merchantId,
    email,
    roles,
    name,
    phone,
  }: {
    merchantId: string;
    email: string;
    roles: MerchantRoleId[];
    name: string;
    phone: string;
  }) {
    return this.fetch<object, object>({
      endpoint: `merchants/${merchantId}/accounts`,
      method: "POST",
      queryParameters: {},
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v08,
      body: {
        merchantId,
        email,
        roles,
        name,
        phone,
      },
    });
  }

  /**
   * GET /api/merchants/{merchantId}/roles
   * Returns a list of roles for a given merchant
   */
  public getMerchantRoles({ merchantId }: { merchantId: string }) {
    return this.fetch<object, MerchantRole[]>({
      endpoint: `merchants/${merchantId}/roles`,
      method: "GET",
      queryParameters: {},
    });
  }

  /**
   * PUT /api/accounts/{userId}/disable
   */
  public disableMerchantAccount({ userId }: { userId: string }) {
    return this.fetch<object, object>({
      endpoint: `accounts/${userId}/disable`,
      method: "PUT",
      queryParameters: {},
    });
  }

  /**
   * PUT /api/accounts/{userId}/enable
   */
  public enableMerchantAccount({ userId }: { userId: string }) {
    return this.fetch<object, object>({
      endpoint: `accounts/${userId}/enable`,
      method: "PUT",
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v08,
      queryParameters: {},
    });
  }

  public updateMerchantAccount({
    merchantId,
    userId,
    roles,
    name,
    phone,
  }: {
    merchantId: string;
    userId: string;
    roles?: MerchantRoleId[];
    name?: string;
    phone?: string;
  }) {
    return this.fetch<object, object>({
      endpoint: `merchants/${merchantId}/accounts/${userId}`,
      method: "PUT",
      queryParameters: {},
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v08,
      body: {
        ...(roles && { roles }),
        ...(name && { name }),
        ...(phone && { phone }),
      },
    });
  }

  /**
   * GET /api/accounts/self
   * Returns the current user's account information
   */
  public getAccountInformationSelf() {
    return this.fetch<
      object,
      {
        id: string;
        name: string | null;
        email: string;
        permissions: PermissionItem[];
        phone: string | null;
      }
    >({
      endpoint: `accounts/self`,
      method: "GET",
      queryParameters: {},
    });
  }

  /**
   * GET /api/agents
   */
  public getAgents() {
    return this.fetch<ParamsWithApplicationId<object>, Agent[]>({
      endpoint: "agents",
      method: "GET",
      queryParameters: { applicationId: this.applicationId },
      acceptHeader: VOLUME_API_VERSION_HEADERS.v1,
    });
  }

  /**
   * GET /api/agents/{agentId}
   */
  public getAgent({ agentId }: { agentId: string }) {
    return this.fetch<ParamsWithApplicationId<object>, Agent>({
      endpoint: `agents/${agentId}`,
      method: "GET",
      queryParameters: { applicationId: this.applicationId },
      acceptHeader: VOLUME_API_VERSION_HEADERS.v1,
    });
  }

  /**
   * GET /api/agents/{agentId}/payment-config
   */
  public getAgentPaymentConfig({ agentId }: { agentId: string }) {
    return this.fetch<ParamsWithApplicationId<object>, AgentPaymentConfig>({
      endpoint: `agents/${agentId}/payment-config`,
      method: "GET",
      queryParameters: { applicationId: this.applicationId },
      acceptHeader: VOLUME_API_VERSION_HEADERS.v1,
    });
  }

  /**
   * GET /api/agents/{agentId}/payment-config/history
   */
  public getAgentPaymentConfigHistory({ agentId }: { agentId: string }) {
    return this.fetch<ParamsWithApplicationId<object>, AgentPaymentConfig[]>({
      endpoint: `agents/${agentId}/payment-config/history`,
      method: "GET",
      queryParameters: { applicationId: this.applicationId },
      acceptHeader: VOLUME_API_VERSION_HEADERS.v1,
    });
  }

  async updatePayoutSchedule(
    merchantId: string,
    param: {
      accountId: number;
      applicationId: string;
      schedule: {
        minimumBalance: number;
        minimumBalanceAfterPayout: number;
        days: string[];
        time: string;
        enabled: boolean;
      };
    },
  ) {
    return this.fetch<object, object>({
      endpoint: `merchants/${merchantId}/applications/${this.applicationId}/payouts/schedule`,
      method: "POST",
      queryParameters: {},
      contentTypeHeader: VOLUME_API_VERSION_HEADERS.v07,
      body: param,
    });
  }
}

export const volumeApiFetch = async ({
  accessToken,
  endpoint,
  method,
  queryParameters,
  body,
  headers,
}: {
  accessToken: string;
  endpoint: string;
  method: VolumeApiMethod;
  queryParameters?: string;
  body?: object;
  headers?: Record<string, string>;
}) => {
  return fetch(
    `${env.VITE_VOLUME_URL}/api/${endpoint}${queryParameters ? `?${queryParameters}` : ""}`,
    {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...COMMON_FETCH_HEADERS,
        ...headers,
      },
      ...(body && { body: JSON.stringify(body) }),
    },
  );
};
